/**
 * Money & rate parsing/formatting. Domain rule (AGENTS.md): money is integer cents,
 * rates are basis points — floats never touch stored amounts.
 */

/**
 * Parses user input like "1.234,56", "1234.56", "-500", "1,234.56" into integer cents.
 * Returns null when the input is not a clean amount.
 */
export function parseAmountToCents(input: string): number | null {
  const raw = input.trim().replace(/[€\s]/g, '');
  if (!raw) return null;
  const match = raw.match(/^(-?)([\d.,]+)$/);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  let digits = match[2];

  const lastComma = digits.lastIndexOf(',');
  const lastDot = digits.lastIndexOf('.');
  let decimalSep = '';
  if (lastComma >= 0 && lastDot >= 0) {
    decimalSep = lastComma > lastDot ? ',' : '.';
  } else if (lastComma >= 0) {
    // "1,234" is ambiguous — treat a single comma with exactly 3 trailing digits as thousands.
    decimalSep = digits.length - lastComma - 1 === 3 && digits.indexOf(',') === lastComma ? '' : ',';
  } else if (lastDot >= 0) {
    decimalSep = digits.length - lastDot - 1 === 3 && digits.indexOf('.') === lastDot ? '' : '.';
  }

  let integerPart = digits;
  let fractionPart = '';
  if (decimalSep) {
    const sepIndex = decimalSep === ',' ? lastComma : lastDot;
    integerPart = digits.slice(0, sepIndex);
    fractionPart = digits.slice(sepIndex + 1);
    if (fractionPart.length > 2 || /[.,]/.test(fractionPart)) return null;
    // The decimal separator must not double as a grouping separator ("1,2,3").
    if (integerPart.includes(decimalSep)) return null;
  }
  integerPart = integerPart.replace(/[.,]/g, '');
  if (!/^\d*$/.test(integerPart) || (!integerPart && !fractionPart)) return null;

  const euros = Number.parseInt(integerPart || '0', 10);
  const cents = Number.parseInt(fractionPart.padEnd(2, '0') || '0', 10);
  return sign * (euros * 100 + cents);
}

/** Formats cents for form inputs: 123456 → "1234.56". */
export function centsToInputValue(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Formats cents for display with currency, e.g. -123456 → "-1.234,56 €". */
export function formatCents(cents: number, currency: string = 'EUR', locale: string = 'de-DE'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** Parses percent input like "3,5" or "3.5" into basis points (350). Max 2 decimals. */
export function parsePercentToBps(input: string): number | null {
  const raw = input.trim().replace(/[%\s]/g, '').replace(',', '.');
  if (!raw) return null;
  if (!/^-?\d+(\.\d{1,2})?$/.test(raw)) return null;
  return Math.round(Number.parseFloat(raw) * 100);
}

/** Formats basis points as percent string: 350 → "3.50". */
export function bpsToPercentInput(bps: number): string {
  const sign = bps < 0 ? '-' : '';
  const abs = Math.abs(bps);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Display helper: 350 → "3,50 %". */
export function formatBpsAsPercent(bps: number, locale: string = 'de-DE'): string {
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(bps / 100)} %`;
}
